import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ErrorToast from '../components/ErrorToast';
import ErrorModal from '../components/ErrorModal';
import { setTokenRefreshErrorHandler } from './AuthContext';

const ErrorContext = createContext();

export const ErrorProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [modals, setModals] = useState([]);

  // Show error toast
  const showErrorToast = useCallback((error, options = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      error,
      autoHide: true,
      duration: 5000,
      ...options
    };
    
    setToasts(prev => [...prev, toast]);
    
    return id;
  }, []);

  // Show error modal
  const showErrorModal = useCallback((error, options = {}) => {
    const id = Date.now() + Math.random();
    const modal = {
      id,
      error,
      isOpen: true,
      ...options
    };
    
    setModals(prev => [...prev, modal]);
    
    return id;
  }, []);

  // Dismiss toast
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Close modal
  const closeModal = useCallback((id) => {
    setModals(prev => prev.filter(modal => modal.id !== id));
  }, []);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Clear all modals
  const clearAllModals = useCallback(() => {
    setModals([]);
  }, []);

  // Handle API errors with consistent messaging
  const handleApiError = useCallback((error, context = '') => {
    let userMessage = 'An unexpected error occurred. Please try again';
    
    if (error.code) {
      switch (error.code) {
        case 'permission-denied':
          userMessage = 'You do not have permission to perform this action';
          break;
        case 'unavailable':
          userMessage = 'Service is temporarily unavailable. Please try again later';
          break;
        case 'unauthenticated':
          userMessage = 'Please log in to continue';
          break;
        case 'not-found':
          userMessage = 'The requested resource was not found';
          break;
        case 'already-exists':
          userMessage = 'This item already exists';
          break;
        case 'failed-precondition':
          userMessage = 'This action cannot be completed at this time';
          break;
        case 'resource-exhausted':
          userMessage = 'Too many requests. Please wait a moment and try again';
          break;
        case 'cancelled':
          userMessage = 'Operation was cancelled';
          break;
        case 'deadline-exceeded':
          userMessage = 'Request timed out. Please try again';
          break;
        default:
          if (error.message) {
            userMessage = error.message;
          }
      }
    } else if (error.message) {
      userMessage = error.message;
    }

    return showErrorToast(userMessage, {
      variant: 'error',
      autoHide: true,
      duration: 5000
    });
  }, [showErrorToast]);

  // Handle network errors with explicit offline detection
  const handleNetworkError = useCallback((error) => {
    // Import offline detection utility dynamically to avoid circular dependencies
    import('../utils/networkUtils').then(({ isOnline, isNetworkError: checkIsNetworkError }) => {
      let userMessage = 'Network error. Please check your connection and try again';
      
      // Use explicit offline detection
      const isOffline = !isOnline();
      const isNetworkErr = checkIsNetworkError(error);
      
      if (isOffline) {
        userMessage = 'You appear to be offline. Please check your internet connection and try again';
      } else if (error.message?.includes('timeout') || error.code === 'deadline-exceeded') {
        userMessage = 'Request timed out. Please check your connection and try again';
      } else if (error.message?.includes('offline') || isNetworkErr) {
        userMessage = 'Network error. Please check your connection and try again';
      }

      showErrorToast(userMessage, {
        variant: 'error',
        autoHide: true,
        duration: 7000
      });
    }).catch(() => {
      // Fallback to original behavior if import fails
      let userMessage = 'Network error. Please check your connection and try again';
      
      if (error.message?.includes('timeout')) {
        userMessage = 'Request timed out. Please check your connection and try again';
      } else if (error.message?.includes('offline')) {
        userMessage = 'You appear to be offline. Please check your connection';
      }

      showErrorToast(userMessage, {
        variant: 'error',
        autoHide: true,
        duration: 7000
      });
    });
  }, [showErrorToast]);

  // Handle validation errors
  const handleValidationError = useCallback((error) => {
    const userMessage = error.message || 'Please check your input and try again';
    
    return showErrorToast(userMessage, {
      variant: 'warning',
      autoHide: true,
      duration: 4000
    });
  }, [showErrorToast]);

  // Handle critical errors (show modal)
  const handleCriticalError = useCallback((error, title = 'Critical Error') => {
    const userMessage = error.message || 'A critical error occurred. Please refresh the page';
    
    return showErrorModal(userMessage, {
      title,
      variant: 'critical',
      showRetry: true
    });
  }, [showErrorModal]);

  // Handle token refresh errors (Phase 4: Error Handling & User Feedback)
  const handleTokenRefreshError = useCallback((error, options = {}) => {
    let userMessage = 'Session expired. Please log in again';
    let shouldForceReLogin = false;
    
    // Check error type to determine appropriate user message
    if (error.code) {
      switch (error.code) {
        case 'auth/user-token-expired':
          userMessage = 'Your session has expired. Please log in again';
          shouldForceReLogin = true;
          break;
        case 'auth/invalid-user-token':
          userMessage = 'Your session is invalid. Please log in again';
          shouldForceReLogin = true;
          break;
        case 'auth/user-disabled':
          userMessage = 'Your account has been disabled. Please contact support';
          shouldForceReLogin = true;
          break;
        case 'auth/user-not-found':
          userMessage = 'Your account was not found. Please log in again';
          shouldForceReLogin = true;
          break;
        case 'auth/network-request-failed':
          // Network error during token refresh - temporary, don't force re-login
          userMessage = 'Network error during session refresh. Please check your connection';
          shouldForceReLogin = false;
          break;
        case 'auth/too-many-requests':
          userMessage = 'Too many requests. Please wait a moment and try again';
          shouldForceReLogin = false;
          break;
        default:
          if (error.message) {
            userMessage = error.message;
          }
      }
    } else if (error.message) {
      userMessage = error.message;
    }
    
    // Determine if we should show toast or modal
    if (shouldForceReLogin || options.forceReLogin) {
      // Show modal for critical authentication failures
      return showErrorModal(userMessage, {
        title: 'Session Expired',
        variant: 'critical',
        showRetry: false,
        onRetry: options.onRetry, // Allow custom retry handler (e.g., redirect to login)
        ...options
      });
    } else {
      // Show toast for temporary errors
      return showErrorToast(userMessage, {
        variant: 'warning',
        autoHide: true,
        duration: options.duration || 6000,
        ...options
      });
    }
  }, [showErrorToast, showErrorModal]);

  // Register token refresh error handler with AuthContext (Phase 4)
  useEffect(() => {
    setTokenRefreshErrorHandler(handleTokenRefreshError);
    
    return () => {
      // Cleanup: unregister handler when ErrorProvider unmounts
      setTokenRefreshErrorHandler(null);
    };
  }, [handleTokenRefreshError]);

  const value = {
    // Toast methods
    showErrorToast,
    dismissToast,
    clearAllToasts,
    
    // Modal methods
    showErrorModal,
    closeModal,
    clearAllModals,
    
    // Specialized error handlers
    handleApiError,
    handleNetworkError,
    handleValidationError,
    handleCriticalError,
    handleTokenRefreshError, // Phase 4: Token refresh error handling
    
    // State
    toasts,
    modals
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      
      {/* Render toasts */}
      {toasts.map(toast => (
        <ErrorToast
          key={toast.id}
          error={toast.error}
          onDismiss={() => dismissToast(toast.id)}
          autoHide={toast.autoHide}
          duration={toast.duration}
          variant={toast.variant}
        />
      ))}
      
      {/* Render modals */}
      {modals.map(modal => (
        <ErrorModal
          key={modal.id}
          isOpen={modal.isOpen}
          onClose={() => closeModal(modal.id)}
          error={modal.error}
          title={modal.title}
          onRetry={modal.onRetry}
          showRetry={modal.showRetry}
          variant={modal.variant}
        />
      ))}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
